/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useDispatch, useSelector } from 'react-redux'; // Importing hooks for Redux dispatch and selector
import {useEffect, useState, useRef} from 'react'; // Importing React hooks
import { useTranslation } from 'react-i18next';
import {calculateHeight, errorMsg, isSmWidth} from "../../../../../../../Apis/functions";
import routes from "../../../../../../../Routes/routes";
import {useLocation, useNavigate} from "react-router-dom";
import {
    AdjustAttendanceChangeRequestSubmission,
    GetAttendanceAdjustmentGroupById,
    GetAttendancePeriodRequest
} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";
import {toast} from "react-toastify";
import {ROW_ATTENDANCE_ADJUSTED, SUBMISSION_PERIOD_REVIEW_ITEM_MODEL} from "../Model/Model";
import {PHOTO_PICKER_MODEL} from "../../../../../../../components/PhotoPicker/SubComponent/Models/Models";
import HandleChange from "../../../../../../../Helpers/HandleChange";
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Model/Model";
import {
    ClearRowsAdjusted
} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentPersistSlice";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";

const ConfirmChangeRequestApp = () => {
    const navigate = useNavigate(); // Initialize navigate function for routing
    const { t } = useTranslation(); // Translation hook to handle language translations
    const dispatch = useDispatch(); // Redux dispatch hook
    const location = useLocation();
    const {requestId} = location.state;   // Access route state (e.g. ID passed from previous page)
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development data from Redux store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux store
    const {getAttendancePeriodRequest } = useSelector((state) => state.AttendanceAdjustmentSlice);
    const { adjustedChangeRequest, adjustedChangeRequestId } = useSelector((state) => state.AttendanceAdjustmentPersistSlice);
    const { isAttendanceSupervisor } = useSelector((state) => state.AttendancePersistSlice);
    const [modified, setModified] = useState(false);
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [periodReviewItem, setPeriodReviewItem] = useState({...SUBMISSION_PERIOD_REVIEW_ITEM_MODEL});
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL });
    const [emptyFields, setEmptyFields] = useState(false);
    const { handleChange } = HandleChange({ model: periodReviewItem, setModel: setPeriodReviewItem });
    const [objectItems, setObjectItems] = useState([]);

    /**
     * Adjusts dynamic heights for layout containers.
     *
     * Behavior:
     * - Skips execution on small screens
     * - Calculates and sets height for card and table containers
     * - Uses a fixed bottom offset for layout consistency
     *
     * Runs once on mount.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = 70;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, []);

    /**
     * Initializes required data and maps adjusted rows.
     *
     * Flow:
     * 1. Fetches attendance period request data
     * 2. If successful, loads detailed request info (getInfo)
     * 3. Processes persisted adjusted rows:
     *    - Filters only DRAFT changes
     *    - Resets entry/exit fields if needed
     *    - Maps rows into request payload structure
     *
     * Notes:
     * - Uses Redux persisted state (adjustedChangeRequest)
     * - Runs on mount
     */
    useEffect(() => {
        dispatch(GetAttendancePeriodRequest()).then(
            ({
                 payload: {
                     data: { success, code },
                 }
             }) => {
                if (success){
                    getInfo();
                }else errorMsg(code);
            });

        // Only the rows of this change request may be submitted; anything kept from
        // a previously visited one carries item ids the backend does not know.
        const persisted = adjustedChangeRequestId === requestId ? adjustedChangeRequest : null;

        const modifiedRows = persisted && Object.values(persisted)
            .filter(e => e?.entryStatusAdjusted === STATUS_MODEL.DRAFT || e?.exitStatusAdjusted === STATUS_MODEL.DRAFT)
            .map(e => {
                if(e.entryStatusAdjusted !== STATUS_MODEL.DRAFT){
                    return ROW_ATTENDANCE_ADJUSTED.resetEntryProps(e);
                }else if(e.exitStatusAdjusted !== STATUS_MODEL.DRAFT){
                    return ROW_ATTENDANCE_ADJUSTED.resetExitProps(e);
                }
                return e;
            });

        if(modifiedRows)
            setObjectItems(ROW_ATTENDANCE_ADJUSTED.mapRequestedBatchRows(modifiedRows));
    }, [dispatch]); // Dependency array to trigger re-fetching on update

    /**
     * Refreshes request data after a successful modification.
     *
     * Behavior:
     * - Re-fetches request information
     * - Resets modified flag
     *
     * Triggered when `modified` changes.
     */
    useEffect(() => {
        if(modified){
            getInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    /**
     * Fetches detailed information for the current change request.
     *
     * Behavior:
     * - Retrieves request data by requestId
     * - Maps response into local model (periodReviewItem)
     * - Initializes evidence files for photo picker
     *
     * Error handling:
     * - Displays error message if request fails
     *
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetAttendanceAdjustmentGroupById(requestId)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    setPeriodReviewItem(SUBMISSION_PERIOD_REVIEW_ITEM_MODEL.toSet(data));
                    setPhotoPicker((prev) => ({
                        ...prev,
                        listFiles: data?.evidence?.map((e) => PHOTO_PICKER_MODEL.toSet(e))
                    }));
                }else errorMsg(code);
            }
        );
    };

    /**
     * Navigates back to the change request table view.
     *
     * Preserves navigation state:
     * - Current tab (Submissions)
     * - Current subview (Requested)
     * - Request identifier
     */
    const handleOnBack = () => {
        navigate(routes.submission.changeRequest, {
            state: {
                currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED,
                requestId: requestId
            },
        });
    };

    /**
     * Submits updated attendance change request.
     *
     * Flow:
     * 1. Validates required fields using model validation
     * 2. If invalid:
     *    - Marks empty fields state
     *    - Stops execution
     * 3. If valid:
     *    - Prepares payload (files + adjusted items)
     *    - Dispatches update request
     *
     * On success:
     * - Clears persisted adjusted rows
     * - Displays success notification
     * - Redirects to main attendance submissions view
     *
     * On failure:
     * - Displays error message based on response code
     *
     * @returns {void}
     */
    const handleUpdate = () => {
        if (!periodReviewItem.isValidChangeRequest(objectItems)) {
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);
        const files = photoPicker.listFiles;
        dispatch(AdjustAttendanceChangeRequestSubmission(periodReviewItem.toRegisterChangeRequest({ files, items: objectItems }))).then(
            ({
                 payload: {
                     data: { success, code },
                 }
             }) => {
                if (success) {
                    dispatch(ClearRowsAdjusted());
                    toast.success(t("has_been_updated_successfully"));
                    navigate(routes.attendance.main, {
                        state: {
                            currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                            currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUESTED
                        },
                    });
                }else errorMsg(code);
            }
        );
    };

    return {
        DEV,
        permission,
        navigate,
        contentCardRef,
        contentTableRef,
        containerCardRef,
        containerTableRef,
        objectItems,
        handleOnBack,
        getAttendancePeriodRequest,
        handleUpdate,
        periodReviewItem,
        emptyFields,
        handleChange,
        photoPicker,
        setPhotoPicker,
        isAttendanceSupervisor
    }
};

export default ConfirmChangeRequestApp; // Exporting the component for use in other parts of the app
